import React, { useState, useCallback, useMemo } from 'react';
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  DialogContent,
  DialogActions,
  DialogTitle,
  Dialog,
  Alert,
} from '@mui/material';
import { FileUploadArea } from 'app/components';
import { FileType } from 'app/components/FileUploadArea/FileUploadArea.types';
import { useIntl } from 'react-intl';
import { useUploadNaverrekenenTemplateMutation } from 'app/hooks/api/admin';

const MAX_FILE_SIZE = 52428800; // 50 MB

export const NaverrekenenTemplateUploadPage = () => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);
  const intl = useIntl();

  const { mutateAsync } = useUploadNaverrekenenTemplateMutation();

  const resetState = useCallback(() => {
    setUploadedFile(null);
    setIsConfirmed(false);
    setError(null);
  }, []);

  const handleFileUpload = useCallback((files: File[]) => {
    const file = files[0];
    if (!file) return;

    setUploadedFile(file);
    setIsConfirmed(false);
    setError(null);
  }, []);

  const handleConfirmationChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setIsConfirmed(event.target.checked);
  }, []);

  const handleUploadFile = useCallback(async () => {
    if (!uploadedFile) return;

    setIsLoading(true);
    setError(null);

    try {
      await mutateAsync(uploadedFile);
      setShowSuccessDialog(true);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        intl.formatMessage({
          id: 'tools.naverrekenenTemplate.uploadFailed',
        });
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [uploadedFile, mutateAsync, intl]);

  const handleSuccessDialogClose = useCallback(() => {
    setShowSuccessDialog(false);
    resetState();
  }, [resetState]);

  const isUploadReady = useMemo(
    () => !!uploadedFile && isConfirmed && !isLoading,
    [uploadedFile, isConfirmed, isLoading],
  );

  return (
    <div>
      <Typography variant="h5" gutterBottom sx={{ my: 2 }}>
        {intl.formatMessage({ id: 'tools.naverrekenenTemplate.description' })}
      </Typography>

      <FileUploadArea
        maxSize={MAX_FILE_SIZE}
        onChange={handleFileUpload}
        types={[FileType.Excel]}
        filesLimit={1}
        showSuccessMessage={false}
        disabled={isLoading}
      />

      {uploadedFile && (
        <Box sx={{ mt: 3, mb: 2 }}>
          <FormControlLabel
            control={<Checkbox checked={isConfirmed} onChange={handleConfirmationChange} disabled={isLoading} />}
            label={intl.formatMessage({
              id: 'tools.naverrekenenTemplate.confirmCorrectFile',
            })}
          />
        </Box>
      )}

      {error && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      <Box sx={{ mt: 3, mb: 2 }}>
        <Button variant="contained" fullWidth size="large" onClick={handleUploadFile} disabled={!isUploadReady}>
          {intl.formatMessage({
            id: 'button.upload',
          })}
        </Button>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, gap: 2 }}>
          <CircularProgress size={50} />
          <Typography variant="h6">
            {intl.formatMessage({
              id: 'global.processing',
            })}
          </Typography>
        </Box>
      )}
      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onClose={handleSuccessDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {intl.formatMessage({
              id: 'global.success',
            })}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            {intl.formatMessage({
              id: 'upload.file.successMessage',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessDialogClose} variant="contained" color="primary">
            {intl.formatMessage({
              id: 'close',
            })}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
